// @flow
import axios from 'axios';
import i18n from '../i18n';
import { isTokenExpired, getTokenExpirationDate } from './jwt';
import apiService from './api-service';
import type { FormError } from '../store/reducers/auth';

import config from '../config';

type ApiError = {
  response: {
    data: {
      errors: FormError[]
    }
  }
}

type LoginPayload = {
  user: {
    email: string,
    password: string
  },
  tfa_token?: string,
  refresh_token?: boolean
}

type LoginResponse = {
  access_token?: string,
  refresh_token?: string,
  tfa_enabled?: boolean
}

type SignupResponse = {
  access_token: string
}

type TokenRequestResponse = {
  message: string
}

type ValidatePasswordResetResponse = {
  reset_token: {
    token: string
  }
}

type UpdatePasswordResponse = {
  user: {
    email: string,
    email_verified: boolean,
    id: string
  }
}

type VerifyEmailResponse = {
  user: {
    email_verified: boolean
  }
}

type Enroll2faResponse = {
  tfa_url: string
}

type Validate2faResponse = {
  user: {
    email: string,
    email_verified: boolean,
    id: string
  }
}

type AuthUserType = {
  email: string,
  email_verified: boolean,
  tfa_enabled: boolean,
  created_at: string,
  last_ip: string
}

type UserInfoResponse = {
  user: AuthUserType
}

export class AuthService {
  endpoint: string = config.AUTH_ENDPOINT

  get headers(): any {
    return {
      'Content-Type': 'application/json',
      'Accept-Language': i18n.language
    };
  }

  login = (email: string, password: string, tfaToken: ?string, remember?: boolean): Promise<LoginResponse> => {
    const payload: LoginPayload = {
      user: { email: email, password }
    };
    if (tfaToken && tfaToken.length > 0) {
      payload.tfa_token = tfaToken;
    }
    if (remember) {
      payload.refresh_token = true;
    }
    return this.post('/authenticate', payload).then((data) => {
      localStorage.setItem('returning', 'true');
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('returning', 'true');
        localStorage.setItem('expires_at', getTokenExpirationDate(data.access_token));
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
      }
      return data;
    });
  };

  signup = (email: string, password: string): Promise<SignupResponse> => {
    return this.post('/signup', {
      user: {
        email,
        password
      }
    }).then((data) => {
      localStorage.setItem('returning', 'true');
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('expires_at', getTokenExpirationDate(data.access_token));
      return data;
    });
  };

  resetPassword = (email: string): Promise<TokenRequestResponse> => {
    return this.post('/reset-password', {
      user: { email },
      redirect_url: config.RESET_PW_REDIRECT
    });
  }

  validatePasswordReset = (token: string, query: string): Promise<ValidatePasswordResetResponse> => {
    return this.get(`/update-password/${token}${query}`);
  }

  updatePassword = (email: string, password: string, token: string): Promise<UpdatePasswordResponse> => {
    return this.put(`/update-password/${token}`, {
      user: { password, email }
    });
  }

  verifyEmail = (token: string): Promise<VerifyEmailResponse> => apiService.get(`/verify/${token}`);

  requestVerifyLink = (): Promise<TokenRequestResponse> => this.post('/verify-link', {}, {
    Authorization: `Bearer ${this.token || ''}`
  });

  logout = (): Promise<string> => apiService.get('/logout').then((data) => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('from_login');
    localStorage.removeItem('show_welcome_banner');
    return data;
  })

  exchangeToken = (): Promise<LoginResponse> => {
    return this.post('/token', {
      refresh_token: this.refreshToken
    }, {
      Authorization: `Bearer ${this.token || ''}`
    });
  }

  enroll2fa = (): Promise<Enroll2faResponse> => {
    return this.post('/enable-2fa', {}, {
      Authorization: `Bearer ${this.token || ''}`
    });
  }

  validate2fa = (token: string): Promise<Validate2faResponse> => {
    return this.post('/2fa', { tfa_token: token }, {
      Authorization: `Bearer ${this.token || ''}`
    });
  }

  getUserInfo = (): Promise<UserInfoResponse> => {
    return this.get('/userinfo', {
      Authorization: `Bearer ${this.token || ''}`
    });
  }

  handleError(error: ApiError): Promise<FormError[]> {
    const { errors } = error.response.data;
    const checkErrorCodeExistence = (code: string) => i18n.exists(`errors:codes.${code}`);
    const errorsTFunction = i18n.getFixedT(i18n.language, 'errors');

    const translatedErrors = errors.map((err: any) => {
      const translationKey = `errors:codes.${err.code}`;

      if (err.message_code) {
        const messageCodeTranslationKey = `passwordFeedback:all.${err.message_code}`;
        const doesMessageCodeTransaltionExist = i18n.exists(messageCodeTranslationKey);

        if (doesMessageCodeTransaltionExist) {
          const translatedMessage = i18n.t(messageCodeTranslationKey);
          return { ...err, translatedMessage: translatedMessage };
        }
      }

      const doesTranslationExist = checkErrorCodeExistence(err.code);

      if (doesTranslationExist) {
        return { ...err, translatedMessage: errorsTFunction(translationKey) };
      }
      return { ...err, translatedMessage: '' };
    });

    return Promise.reject(translatedErrors);
  }

  get(url: string, headers: any = {}): Promise<any> {
    const opts = Object.assign({}, {
      method: 'get',
      url: `${this.endpoint}${url}`,
      headers: {
        ...this.headers,
        ...headers
      }
    });
    return axios(opts).then((response) => response.data)
      .catch(this.handleError);
  }

  post(url: string, body: any, headers: any = {}): Promise<any> {
    return axios({
      url: `${this.endpoint}${url}`,
      method: 'post',
      data: JSON.stringify(body),
      headers: {
        ...this.headers,
        ...headers
      }
    }).then((response) => response.data)
      .catch(this.handleError);
  }

  put(url: string, body: any): Promise<any> {
    return axios({
      url: `${this.endpoint}${url}`,
      method: 'put',
      data: JSON.stringify(body),
      headers: this.headers
    }).then((response) => response.data)
      .catch(this.handleError);
  }

  get loggedIn(): boolean {
    return !!this.token && !isTokenExpired(this.token);
  }

  get newUser(): boolean {
    return !this.token && !this.refreshToken;
  }

  get returning(): boolean {
    return localStorage.getItem('returning') === 'true';
  }

  get tokenExpired(): boolean {
    return isTokenExpired(this.token);
  }

  get refreshToken(): ?string {
    return localStorage.getItem('refresh_token');
  }

  get token(): ?string {
    return localStorage.getItem('access_token');
  }
}

export default new AuthService();



// WEBPACK FOOTER //
// ./src/services/auth-service.js